/**
 * Agrar-Lexikon — strukturierte Inhalte für das Bodenblick-Wissensmodul.
 * Pro Eintrag: { id, title, icon (Phosphor), summary, details, tip, tags[], badges[] }
 * badges verknüpfen Lexikon-Einträge mit Dashboard-Werten (z.B. "NDVI<0.4").
 */

export const TABELLEN = [
  {
    id: "oeko-score",
    title: "Ökosystem-Gesamtscore",
    icon: "Gauge",
    summary: "Der große Ring ganz oben im Dashboard. Ein einziger Wert (0–100) fasst den Zustand all deiner Flächen zusammen.",
    details:
      "Der Score mittelt die Gesundheit aller beobachteten Regionen. Reinrechnen: NDVI (Vegetation), Bodenfeuchte-Defizit (Wasser), Ertrags-Index (Ausblick) und Wald-Vitalität. Ein grüner Ring über 75 heißt: alle Flächen laufen gut. Sinkt er unter 40, zieht mindestens eine Region das Ensemble nach unten — lohnt sich, gezielt drauf zu schauen.",
    tip: "Screenshot morgens, mit Mittag vergleichen, mittwochs und sonntags Trend vergleichen. Bei Abweichung > 10 Punkten: ins Region-Detail springen.",
    tags: ["Dashboard", "Score", "Gesund­heit"],
  },
  {
    id: "kpi-karten",
    title: "KPI-Karten (4er-Grid)",
    icon: "SquaresFour",
    summary: "Vier kompakte Kacheln mit Ø NDVI, Bodenfeuchte-Defizit, Ertrags-Index und Wald-Vitalität.",
    details:
      "Jede Kachel zeigt einen aktuellen Wert + kleine Delta-Badge (Veränderung) + Trend-Sparkline. Grün/Orange/Rot signalisieren direkt, ob ein Wert positiv oder negativ abweicht. Die Sparkline zeigt die letzten 30 Tage — so erkennst du steile Rückgänge sofort visuell, auch wenn der Zahlenwert noch unauffällig aussieht.",
    tip: "Pfeilrichtung der Sparkline zählt mehr als der Zahlenwert selbst. Eine fallende Linie bei noch grünen Zahlen = Frühwarnung.",
    tags: ["Dashboard", "KPI", "Trend"],
  },
  {
    id: "region-klartext",
    title: "Region-Klartext-Karten",
    icon: "MapPin",
    summary: "Ausführliche Karten pro Fläche mit Vegetations-, Bodenwasser- und Ertrags-Lage plus konkreten Tipps.",
    details:
      "Jede Region bekommt: Kennzeichen (z.B. Feld A), Gesundheitsring, 5–6 Status-Blöcke (NDVI, Abweichung, Bodenfeuchte, Ernte, Preis, ggf. Forst), NDVI-Verlauf der letzten 30 Tage und automatische Handlungsempfehlungen. Die Tipps sind algorithmisch aus den Rohwerten abgeleitet — du musst keine Regeln selber aufstellen.",
    tip: "Rote oder orange Tipps IMMER zuerst lesen. Danach NDVI-Sparkline prüfen: zeigt sie nach unten, plane eine Begehung diese Woche.",
    tags: ["Dashboard", "Region", "Tipps"],
  },
  {
    id: "sat-heatmap",
    title: "Satelliten-Heatmap (Seite Satellite)",
    icon: "GlobeHemisphereEast",
    summary: "Farb-Karte deiner Fläche: grün = vital, gelb/braun = Stress. Slider bewegt sich durch die Zeit.",
    details:
      "Die Heatmap zeigt NDVI (Vegetations-Index) als farblich abgestufte Zellen über deiner Region. Hellgrün = frisches Blattwerk, dunkelgrün = üppig, gelb = Trockenstress, braun = kahl. Mit dem Zeitslider kannst du 30 Tage zurück scrollen und Stress-Ausbrüche lokalisieren.\n\nNeben der Haupt-Heatmap findest du die NDMI-Variante (Feuchtigkeit im Blattgewebe) — wichtig im Sommer, weil NDMI oft **früher** Trockenstress zeigt als NDVI.",
    tip: "Wenn zwei benachbarte Tage starke Unterschiede zeigen, war entweder ein Gewitter oder eine Wolke am Werk — nicht dein Bestand. Bei Wolken-Anomalien den Tag überspringen.",
    tags: ["Satellite", "Heatmap", "Zeitreise"],
  },
  {
    id: "wetter-charts",
    title: "Wetter- & Bodentabelle",
    icon: "CloudSun",
    summary: "Vier Charts: Temperatur, Niederschlag, Bodenfeuchte 0–10 cm, Evapotranspiration (ET₀).",
    details:
      "Niederschlag + ET₀ zusammen sagen dir, ob deine Böden Wasser gewinnen oder verlieren. Fällt ET₀ über 5 mm bei weniger als 5 mm Regen pro Tag, trocknet der Oberboden spürbar aus. Die Bodenfeuchte-Kurve zeigt das dann mit 12–24 h Verzögerung.",
    tip: "Faustregel: ET₀ × 2 mm in den letzten 3 Tagen UND kein Regen = Zeit zum Bewässern, falls du kannst. Die Ampel oben auf der Seite zeigt dir das bereits automatisch.",
    tags: ["Wetter", "Boden", "Bewässerung"],
  },
  {
    id: "correlation",
    title: "Korrelations-Chart (NDVI × Preis)",
    icon: "ChartLineUp",
    summary: "Dual-Achsen-Chart: NDVI (links) gegen Futures-Preis (rechts) über die Zeit.",
    details:
      "Je stärker der Pearson-Koeffizient ρ (oben rechts im Chart), desto synchroner bewegen sich Ernte-Aussicht und Marktpreis. ρ > 0.6 = starker Gleichlauf (schlechter Bestand → Preis steigt). ρ nahe 0 = Markt ignoriert lokale Signale. Negative ρ sind selten und deuten auf Gegenbewegungen (z.B. globale Überschüsse überschreiben lokalen Stress).",
    tip: "Hohe positive Korrelation + sinkender NDVI = gutes Verkaufs­fenster. Bei ρ < 0.3 den Markt eher technisch handeln statt auf Ertragsprognosen zu wetten.",
    tags: ["Correlation", "Preis", "Futures"],
  },
  {
    id: "pipeline-log",
    title: "Pipeline-Log",
    icon: "Terminal",
    summary: "Terminal-artige Übersicht aller ETL-Läufe. Zeigt, ob die Datenabrufe klappen.",
    details:
      "Jede Zeile ist ein Pipeline-Lauf: Zeitpunkt, Status (success/partial/failed) und Fehlerliste. Rote Einträge zeigen Ausfälle eines Upstream-APIs (z.B. Copernicus Token abgelaufen, Open-Meteo Rate-Limit). Die Dashboard-Werte fallen dann zurück auf Mock-/Cache-Daten.",
    tip: 'Bei mehreren „partial" hintereinander: einmal den „Pipeline ausführen"-Button auf dem Dashboard drücken und schauen, ob es transient war.',
    tags: ["Pipeline", "Debug", "Admin"],
  },
];

export const DUENGEMITTEL = [
  {
    id: "stickstoff",
    title: "Stickstoff (N)",
    icon: "Lightning",
    summary: "Hauptnährstoff für Blattmasse und Eiweißbildung. Zeigt sich direkt im NDVI.",
    details:
      "Stickstoff ist der Motor des Blattwachstums. Pflanzen ziehen ihn vor allem während der Hauptentwicklung (Schossen, Schossen→Blüte) aus dem Boden. Fehlt N, werden die Blätter blass-gelb und NDVI sinkt um 0.1–0.2 Punkte innerhalb 10–14 Tagen.\n\nGaben werden meist geteilt: 40 % Andüngung (Vegetationsbeginn), 40 % Schossergabe, 20 % Ährengabe.",
    tip: "Wenn NDVI zwischen 0.3–0.5 stagniert UND Bodenfeuchte ausreichend ist: Nährstoff-Mangel ist wahrscheinlich. N-Nachdüngung prüfen.",
    tags: ["Makro", "Blatt", "NDVI"],
    badges: ["NDVI 0.3–0.5 + Stagnation", "Blätter blass"],
  },
  {
    id: "phosphor",
    title: "Phosphor (P)",
    icon: "Sparkle",
    summary: "Wurzelstarter und Energiespeicher. Unsichtbar in NDVI, aber entscheidend für Etablierung.",
    details:
      "P braucht die Pflanze vor allem in der Jugend-Entwicklung für kräftige Wurzeln und in der Korn-/Kolbenbildung für Energieübertragung. Im Oberboden oft an Tonpartikel gebunden und erst bei höheren Temperaturen verfügbar.\n\nKaltes Frühjahr + niedriges P = violette Verfärbung an Mais- und Rapsblättern.",
    tip: "P-Versorgung regelmäßig per Bodenprobe prüfen (alle 3–5 Jahre). Sensorwerte helfen hier wenig — P ist eine Boden-, keine Blattgeschichte.",
    tags: ["Makro", "Wurzel", "Startdünger"],
    badges: ["Frühjahr kalt", "Jungpflanze"],
  },
  {
    id: "kalium",
    title: "Kalium (K)",
    icon: "Shield",
    summary: "Macht Pflanzen trocken- und frostresistent. Regelt Wasserhaushalt der Zelle.",
    details:
      "K steuert das Öffnen und Schließen der Spaltöffnungen und damit die Wasser-Effizienz. Kaliummangel zeigt sich in schlaffen Blättern und braunen Randnekrosen. Bei hoher ET₀ und K-Mangel verliert die Pflanze überproportional viel Wasser.",
    tip: "Trockenjahr-Versicherung: K ausreichend versorgen, BEVOR die Trockenheit kommt. Ist der Bestand bereits gestresst, ist eine Blattdüngung Notfall-Mittel.",
    tags: ["Makro", "Trockenheit", "Stressabwehr"],
    badges: ["ET₀ hoch", "Randnekrosen"],
  },
  {
    id: "kas",
    title: "Kalkammonsalpeter (KAS 27)",
    icon: "Cube",
    summary: "Standard-Stickstoff: 27 % N, halb Nitrat/halb Ammonium. Schnell + mittelfristig wirksam.",
    details:
      "KAS ist das Brot-und-Butter-N in Mitteleuropa. Nitrat-Anteil wirkt sofort, Ammonium-Anteil über 1–2 Wochen. Granuliert, einfach streubar, verträgt auch angesäuerte Böden durch den Kalkanteil.",
    tip: "Ideal zur zweiten oder dritten N-Gabe. Frühmorgens streuen, wenn Taufeuchte die Körner leicht auflöst — reduziert Verluste.",
    tags: ["Dünger", "N", "Granulat"],
    badges: ["Schossergabe", "Ährengabe"],
  },
  {
    id: "harnstoff",
    title: "Harnstoff (46 % N)",
    icon: "Drop",
    summary: "Hochkonzentrierter Stickstoff. Günstig, aber anfällig für Ammoniak-Verluste.",
    details:
      "Harnstoff muss erst von Boden-Mikroorganismen in Ammonium und Nitrat umgewandelt werden — das dauert 2–5 Tage. Bei trockener Oberfläche und Wärme verdunstet Stickstoff als Ammoniak in die Luft (bis zu 30 % Verlust).",
    tip: "Nur bei Regen-Vorhersage innerhalb 24 h oder direkt einarbeiten. Stabilisatoren (Urease-Hemmer) senken Verluste deutlich.",
    tags: ["Dünger", "N", "Kosten"],
    badges: ["Vor Regen", "Einarbeiten"],
  },
  {
    id: "guelle",
    title: "Gülle / Festmist",
    icon: "Recycle",
    summary: "Organischer Dünger aus Tierhaltung. Liefert N, P, K + Humus in einem.",
    details:
      "Rindergülle enthält ca. 3–5 kg N, 1–2 kg P₂O₅, 4–6 kg K₂O pro m³. Schweinegülle ist N- und P-reicher. Wirkung ist geteilt: etwa 60 % N sind im ersten Jahr pflanzenverfügbar, der Rest wird über Folgejahre frei.\n\nHumusaufbau: jede Tonne Festmist bringt 10–15 kg Dauerhumus, das erhöht langfristig die Wasserspeicherfähigkeit.",
    tip: "Nach Ausbringung zügig einarbeiten (Schleppschlauch/Schleppschuh) um Geruch und N-Verluste zu minimieren. Analyse der eigenen Gülle alle 2 Jahre lohnt sich immer.",
    tags: ["Organisch", "Humus", "Kreislauf"],
    badges: ["Nach Ernte", "Wintergetreide"],
  },
  {
    id: "kompost",
    title: "Kompost / Zwischenfrucht-Mulch",
    icon: "Leaf",
    summary: "Langsam wirkender Boden-Verbesserer. Liefert weniger N, dafür viel Humus und Mikrobiom.",
    details:
      "Reifer Kompost hat ca. 1–2 % N, wovon nur 5–10 % im ersten Jahr freigesetzt werden. Klingt wenig — aber Kompost bringt Leben: Regenwürmer, Pilze, Bakterien. Dadurch wird der Boden locker, speichert mehr Wasser und setzt seinen eigenen Vorrat besser frei.",
    tip: "Jährlich 5–10 t/ha flach einarbeiten. In Kombination mit Zwischenfrüchten (Senf, Ölrettich) wirkt es exponentiell besser.",
    tags: ["Organisch", "Bodenleben", "Langfristig"],
    badges: ["Herbst", "Gemüsebau"],
  },
  {
    id: "mikronaehrstoffe",
    title: "Mikronährstoffe (Mg, S, Mn, Zn, Cu, B)",
    icon: "Atom",
    summary: "Kleine Mengen, große Wirkung. Mangel bleibt oft unentdeckt bis NDVI plötzlich einbricht.",
    details:
      "Schwefel fehlt heute häufiger (weniger saurer Regen als früher) — bei Raps und Getreide kritisch. Magnesium ist Baustein des Chlorophylls; Mangel zeigt sich als fahlgrün-gelbe Blattadern. Mangan und Bor sind Sandböden-Themen.",
    tip: "Bei unerklärlichem NDVI-Rückgang OHNE Wasser- oder N-Problem: Blattdüngung mit Mehrnährstoff-Mix (Bittersalz, Mn, B) probieren. Wirkung in 5–10 Tagen sichtbar.",
    tags: ["Mikro", "Blattdüngung", "Diagnose"],
    badges: ["NDVI plötzlich ↓", "Sandboden"],
  },
];

export const FELDFRUECHTE = [
  {
    id: "winterweizen",
    title: "Winterweizen",
    icon: "Grains",
    summary: "Brotgetreide Nummer 1 in Mitteleuropa. NDVI-Kurve mit markantem Sommer-Plateau.",
    details:
      "Aussaat Oktober, Ernte Juli/August. Typische NDVI-Kurve: Herbst-Anlauf auf 0.3–0.5, Winter-Tief, Frühjahrs-Anstieg auf 0.7–0.9 zur Blüte, dann Abfall Richtung Ernte (Abreife).\n\nWasserbedarf-Spitze: Schossen bis Blüte (Mai–Juni). Trockenstress in dieser Phase kostet 10–30 % Ertrag.",
    tip: "Wenn NDVI im April nicht über 0.6 klettert: Andüngung prüfen. Fällt NDVI in der Abreife zu schnell (< 14 Tage von 0.7 auf 0.4): Ernte frühzeitig organisieren.",
    tags: ["Getreide", "Winter", "Brotfrucht"],
    badges: ["NDVI-Peak 0.8", "Juli Ernte"],
  },
  {
    id: "mais",
    title: "Mais (Silo/Körner)",
    icon: "Plant",
    summary: "Wärmeliebend, trockenheits-empfindlich. NDVI steigt spät, aber steil.",
    details:
      "Aussaat Ende April, Ernte September (Silo) bis Oktober (Körner). NDVI bleibt bis Juni unter 0.4, dann Anstieg auf 0.8–0.9 im Juli. Wasserbedarfs-Peak: Juli (Reihenschluss + Kolben­ansatz).\n\nBesonders empfindlich bei Frost nach Aufgang und bei Hitze > 32 °C zur Blüte.",
    tip: "Bodentemperatur < 8 °C bei Aussaat = Keimungs-Stress. Bei GDD-Lauf unter 15 °C/Tag im Mai: Aussaat lieber 7 Tage nach hinten schieben.",
    tags: ["C4-Pflanze", "Sommer", "Silage"],
    badges: ["Keimung > 8 °C", "Juli Spitze"],
  },
  {
    id: "winterraps",
    title: "Winterraps",
    icon: "Flower",
    summary: "Gelber Frühblüher. Reagiert extrem auf Schwefel und Frost.",
    details:
      "Aussaat August, Ernte Juli. Vor Winter 8–10 Blätter ideal, Wurzelhals > 8 mm. NDVI-Kurve: Herbst-Peak bei 0.5–0.6, Winter-Absenkung, Frühjahrs-Explosion zur Blüte (gelb = 0.7+).\n\nSchwefel-Bedarf etwa 40–60 kg S/ha — im Frühjahr obligatorisch.",
    tip: "Fehlt Schwefel, bleibt die Blüte blass und NDVI erreicht kaum 0.5. Zweite N-Gabe mit schwefelhaltigem Dünger (Piamon, ASS) kombinieren.",
    tags: ["Öl-Pflanze", "Winter", "Schwefel"],
    badges: ["Blüte April/Mai", "S-Bedarf hoch"],
  },
  {
    id: "gerste",
    title: "Winter- & Sommergerste",
    icon: "Grains",
    summary: "Robuste Getreideart. Winter-Variante hat frühesten Ernte-Termin.",
    details:
      "Wintergerste: Aussaat September, Ernte Juni (früheste aller Getreide!). Sommergerste wird Ende März gesät und Juli geerntet — wichtig als Brau-Gerste.\n\nGerste verträgt Trockenheit besser als Weizen, reagiert dafür empfindlicher auf Staunässe.",
    tip: "Bei Staunässe (Bodenfeuchte > 0.4 m³/m³ über 5 Tage) und gleichzeitig NDVI-Stagnation: Gerste wird gelb. Dränage prüfen oder Sorte wechseln.",
    tags: ["Getreide", "Brau", "Früh"],
    badges: ["Ernte Juni", "Staunässe-empfindlich"],
  },
  {
    id: "kartoffel",
    title: "Kartoffeln",
    icon: "Leaf",
    summary: "Knollengewächs mit hohem Wasserbedarf und kritischer Krautfäule-Saison.",
    details:
      "Pflanzung April/Mai, Ernte August–Oktober. NDVI erreicht 0.7–0.8 zur Reihenschluss, bleibt lange hoch. Blatt-Vergilbung Ende Saison ist normal (Kraut-Abreife).\n\nWasser-Kipppunkt: Knollen­ansatz bis Knollenwachstum (Juni–Juli) — Trockenheit halbiert hier den Ertrag.",
    tip: "Bei warmen Nächten + hoher Luftfeuchte: Krautfäule-Risiko steigt. Satelliten-Schatten in Reihen-Struktur kann erste Infektion anzeigen. Fungizid-Spritzung abstimmen.",
    tags: ["Knolle", "Wasser", "Fäule"],
    badges: ["Wasser Juni/Juli", "Krautfäule"],
  },
  {
    id: "soja",
    title: "Soja",
    icon: "Circle",
    summary: "Eiweiß­pflanze, bindet eigenen Stickstoff. Spätsaat, kurze Saison.",
    details:
      "Aussaat Mai, Ernte September/Oktober. NDVI-Verlauf sanft — Peak meist nur bei 0.6–0.7. Das ist normal, weil Soja dichter und niedriger baut als Mais.\n\nStickstoff kommt aus Knöllchen-Bakterien (Rhizobien). Impfung des Saatguts vor Aussaat ist entscheidend.",
    tip: "Kein Stickstoff mineralisch nachdüngen — das bremst die Bakterien-Symbiose. Bei Trockenheit zur Blüte: Bewässerung, falls möglich, sonst Ertrag fällt um 20–40 %.",
    tags: ["Hülsen", "N-Fixierung", "Eiweiß"],
    badges: ["Kein N-Dünger", "Blüte Juli"],
  },
  {
    id: "obstbeeren",
    title: "Obst & Beeren",
    icon: "Tree",
    summary: "Dauerkultur mit jährlicher Vegetations-Kurve. Blüte früh, Ernte gestaffelt.",
    details:
      "Apfel, Birne, Heidelbeere, Himbeere: NDVI steigt ab März auf 0.6+, bleibt bis September hoch, fällt im Oktober/November. Frostgefahr zur Blüte (April/Mai) kann eine komplette Ernte vernichten.\n\nBewässerung meist per Tropfsystem — Bodenfeuchte im Wurzelraum 0.25–0.35 m³/m³ halten.",
    tip: "In Frostnächten (Temperatur < -1 °C zur Blüte): Frostberegnung starten, wenn verfügbar. Das Eis um die Knospe schützt durch Kristallisations-Wärme.",
    tags: ["Dauerkultur", "Frost", "Tropfbewässerung"],
    badges: ["Blüte April/Mai", "Tropfbewässerung"],
  },
  {
    id: "wald",
    title: "Wald (Fichte, Buche, Eiche)",
    icon: "TreeEvergreen",
    summary: "Dauerbestand. NDVI-Schwankung gering, dafür sind Frühwarn-Signale (NDMI) gold wert.",
    details:
      "Laubwald: NDVI 0.7–0.9 Mai–Oktober, nahe 0 im Winter. Nadelwald ganzjährig 0.5–0.7. Borkenkäfer-Befall senkt NDMI oft 2–4 Wochen bevor NDVI reagiert — die Blätter sind dann noch grün, aber schon trocken.",
    tip: "Fällt NDMI im Sommer unter 0.2 bei gleichzeitig normalem NDVI: Bestand grün-bereiten, einzelne Befallstellen suchen. Bei Bestätigung zügig aufarbeiten, um Ausbreitung zu stoppen.",
    tags: ["Dauerkultur", "Borkenkäfer", "NDMI"],
    badges: ["NDMI < 0.2 Sommer", "Kronentrockenheit"],
  },
];

export const MASSNAHMEN = [
  {
    id: "pfluegen",
    title: "Pflügen / Grubbern / Direktsaat",
    icon: "ForkKnife",
    summary: "Drei Wege, den Boden für die Aussaat vorzubereiten — mit sehr unterschiedlichen Folgen.",
    details:
      "**Pflug** wendet den Boden bis 30 cm. Beerdigt Unkraut und Stroh — aber zerstört Regenwurm-Gänge und fördert Erosion. Kurzfristig sauber, langfristig teuer fürs Bodenleben.\n\n**Grubber** lockert bis 15 cm ohne zu wenden. Kompromiss zwischen Unkraut-Management und Bodenschutz.\n\n**Direktsaat** schneidet nur einen Schlitz — maximaler Boden- und Humus-Schutz, aber anspruchsvoll im Unkraut-Management.",
    tip: "Bei gesundem NDVI über mehrere Jahre UND hohem Humusgehalt: mutig auf Minimalbodenbearbeitung umstellen. Bei Problemfeldern (Verdichtung, Pilz) erstmal Grubber.",
    tags: ["Boden", "Saatbett"],
    badges: ["Herbst", "Vor Saat"],
  },
  {
    id: "aussaat",
    title: "Aussaat",
    icon: "Seedling",
    summary: "Das Fenster für optimale Aussaat ist oft nur 5–10 Tage breit.",
    details:
      "Entscheidend sind Bodentemperatur (Mais > 8 °C, Getreide > 5 °C), Bodenfeuchte (feinkrümeliges Saatbett, aber nicht nass) und GDD-Prognose. Zu frühe Aussaat = Auflauf-Probleme + Pilz. Zu spät = Ertrags-Einbußen bei Sommerkulturen.",
    tip: "Dein Bodenblick-Dashboard zeigt GDD und Bodenfeuchte. Wenn GDD-Anstieg > 5 °C pro Tag UND Bodenfeuchte zwischen 0.25–0.35: drei Tage Zeit zur Aussaat.",
    tags: ["Start", "Timing"],
    badges: ["GDD hoch", "Boden 0.25–0.35"],
  },
  {
    id: "duengung",
    title: "Düngung (Gaben-Strategie)",
    icon: "Sparkle",
    summary: "Weniger, dafür häufiger — das ist die moderne Regel. Bedarfs-orientiert statt nach Kalender.",
    details:
      "Statt 180 kg N/ha auf einmal: 60/60/60 in drei Gaben verteilt. Vorteile: weniger Auswaschung, bessere N-Effizienz, höhere Protein-Qualität.\n\nZeitpunkte bei Weizen: Vegetationsbeginn (Andüngung), Schossen (Schossergabe), Ähre-Schieben (Qualitätsgabe).",
    tip: "Vor jeder Gabe kurz auf NDVI schauen: Stagniert er? Dann lohnt die Gabe. Steigt er ungebremst? Dann vielleicht kleiner ausfallen lassen. Das spart Geld und schont Grundwasser.",
    tags: ["N", "Timing", "Nährstoff"],
    badges: ["NDVI stagniert", "Vegetation aktiv"],
  },
  {
    id: "pflanzenschutz",
    title: "Pflanzenschutz (Fungi-/Insekti-/Herbizide)",
    icon: "Shield",
    summary: "Ziel ist Integrierter Pflanzenschutz: biologisch & mechanisch zuerst, Chemie nur im Bedarfsfall.",
    details:
      "**Fungizide**: Blattkrankheiten (Septoria, Rost, Mehltau). Meist ab BBCH 37 (Fahnenblatt) bis Blüte. **Insektizide**: Rapsglanzkäfer, Blattläuse, Kartoffelkäfer. **Herbizide**: gegen Unkraut, idealerweise selektiv.\n\nBodenblick-Daten helfen bei der Prognose: lange Feuchteperioden + wärme-Regen = Pilzdruck hoch.",
    tip: "Nicht prophylaktisch spritzen. Bei Bodenfeuchte > 0.35 über 7+ Tage + Temperatur > 18 °C: Pilzdruck-Monitoring vor Ort intensivieren. Schad­schwellen-Konzept nutzen.",
    tags: ["Schutz", "Schaderreger"],
    badges: ["Feuchte + Wärme", "BBCH 37+"],
  },
  {
    id: "bewaesserung",
    title: "Bewässerung",
    icon: "Drop",
    summary: "Nicht Wasser, sondern das RICHTIGE TIMING entscheidet über Ertrag.",
    details:
      "Oberboden 0–10 cm trocknet schnell, ist für Keimung wichtig. Unterboden 10–40 cm liefert in Wachstumsphase. Ziel: Unterboden zwischen 0.2–0.3 m³/m³ halten.\n\nBewässerungs-Zeitpunkt: früh morgens oder abends. Mittags kostet 30 % mehr Wasser durch Verdunstung.",
    tip: "Wenn Bodenfeuchte-Defizit > 0.05 m³/m³ UND ET₀ > 4 mm/Tag UND kein Regen in 3-Tage-Prognose: bewässern. Die Dashboard-Ampel zeigt das automatisch.",
    tags: ["Wasser", "ET₀"],
    badges: ["Defizit > 0.05", "Hitze"],
  },
  {
    id: "ernte",
    title: "Ernte",
    icon: "Package",
    summary: "Fruchtfolge des Ertrags: Reife treffen + Wetter­fenster nutzen.",
    details:
      "Getreide-Feuchte idealerweise 14–16 %. Über 18 %: teurer Trocknungs­aufwand. Bei Raps: 9 % Kornfeuchte bei Ernte, Öl-Qualität steigt mit kühler Nacht-Ernte.\n\nNDVI-Abfall von 0.7 → 0.3 in 2–3 Wochen = finale Abreife. Ab 0.3 ist das Korn erntereif.",
    tip: "Dein NDVI-Sparkline zeigt den Abreife-Verlauf. Fällt NDVI schneller als 0.05/Tag: frühzeitig Ernte organisieren. Steht stabil: abwarten und Feuchte manuell messen.",
    tags: ["Ernte", "Abreife"],
    badges: ["NDVI < 0.3", "Juli/August"],
  },
  {
    id: "mulchen",
    title: "Mulchen & Zwischenfrucht",
    icon: "Leaf",
    summary: "Gratis-Nährstoffe + Humus + Erosionsschutz. Der unterschätzte Hebel.",
    details:
      "Nach der Ernte Senf, Ölrettich, Phacelia oder Winterrroggen als Zwischenfrucht säen. Diese Pflanzen fixieren Rest-Stickstoff (der sonst ausgewaschen wird), schützen vor Erosion und bauen im Winter Humus auf.\n\nIm Frühjahr werden sie grundgemulcht oder mit Direktsaat überbaut.",
    tip: "Perfekt nach NDVI-Abfall < 0.3 direkt säen. Bodenfeuchte muss > 0.2 sein, sonst keimen sie nicht. Leguminose-Mix (Wicke, Klee) bringt zusätzlich N-Fixierung.",
    tags: ["Humus", "Kreislauf", "Winter"],
    badges: ["Nach Ernte", "Winter-Deckung"],
  },
];

export const TABS = [
  { id: "tabellen", title: "Tabellen erklärt", icon: "Table", items: TABELLEN, accent: "#00FA9A" },
  { id: "duenger", title: "Düngemittel", icon: "Drop", items: DUENGEMITTEL, accent: "#FFC24B" },
  { id: "fruechte", title: "Feldfrüchte", icon: "Plant", items: FELDFRUECHTE, accent: "#7EE8B0" },
  { id: "massnahmen", title: "Bearbeitungsmaßnahmen", icon: "Toolbox", items: MASSNAHMEN, accent: "#4DA3FF" },
];
